import { useMemo, useState, type FC } from "react";
import DeviceBrowserChart from "@/pages/CampaignOverviewPage/charts/DeviceBrowserChart";
import AudienceSegmentTable from "@/pages/CampaignOverviewPage/components/AudienceSegmentTable";
import TopPerformerTile from "@/pages/CampaignOverviewPage/components/TopPerformerTile";
import CreativeCtrPanel from "@/pages/CampaignOverviewPage/widgets/CreativeCtrPanel";
import SizeBubblePanel from "@/pages/CampaignOverviewPage/widgets/SizeBubblePanel";
import { deriveTopPerformers } from "@/pages/CampaignOverviewPage/utils/topPerformers";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/widgets/TopPerformers.module.css";

interface ReportState {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

interface TopPerformersProps {
  audience: ReportState;
  creative: ReportState;
  deviceBrowser: ReportState;
}

type TileKey = "browser" | "creative" | "segment" | "size";

const PANEL_TEXT = TEXT.campaignOverview.topPerformers;

// Tile order is display order, not alphabetical. `source` is the report each
// tile's value is derived from, which decides when it shows a skeleton.
const TILES: {
  key: TileKey;
  label: string;
  source: "audience" | "creative" | "deviceBrowser";
}[] = [
  { key: "creative", label: PANEL_TEXT.tileCreative, source: "creative" },
  { key: "size", label: PANEL_TEXT.tileSize, source: "creative" },
  {
    key: "browser",
    label: PANEL_TEXT.tileBrowser,
    source: "deviceBrowser",
  },
  { key: "segment", label: PANEL_TEXT.tileSegment, source: "audience" },
];

// Renders the tile column and the detail panel as sibling cells so the page
// grid can place them at 1/4 + 3/4. The first tile is selected on load.
const TopPerformers: FC<TopPerformersProps> = ({
  audience,
  creative,
  deviceBrowser,
}) => {
  const [selected, setSelected] = useState<TileKey>("creative");
  const reports = { audience, creative, deviceBrowser };

  const leaders = useMemo(
    () =>
      deriveTopPerformers({
        audienceRows: audience.rows,
        creativeRows: creative.rows,
        deviceBrowserRows: deviceBrowser.rows,
      }),
    [audience.rows, creative.rows, deviceBrowser.rows]
  );

  return (
    <>
      <div className={styles.tiles}>
        <h2 className={styles.sectionLabel}>{PANEL_TEXT.sectionLabel}</h2>
        {TILES.map(({ key, label, source }) => (
          <TopPerformerTile
            key={key}
            isLoading={reports[source].isLoading}
            isSelected={selected === key}
            label={label}
            // A failed refresh leaves the previous scope's rows in the store,
            // so a tile whose report errored must not name a leader from them.
            value={reports[source].error ? null : (leaders[key]?.label ?? null)}
            onSelect={() => setSelected(key)}
          />
        ))}
      </div>
      {selected === "creative" && (
        <CreativeCtrPanel
          error={creative.error}
          isLoading={creative.isLoading}
          leaderKey={leaders.creative?.key ?? null}
          rows={creative.rows}
        />
      )}
      {selected === "size" && (
        <SizeBubblePanel
          error={creative.error}
          isLoading={creative.isLoading}
          rows={creative.rows}
        />
      )}
      {selected === "browser" && (
        <DeviceBrowserChart
          error={deviceBrowser.error}
          isLoading={deviceBrowser.isLoading}
          leader={leaders.browser?.label ?? null}
          rows={deviceBrowser.rows}
        />
      )}
      {selected === "segment" && (
        <AudienceSegmentTable
          error={audience.error}
          isLoading={audience.isLoading}
          rows={audience.rows}
        />
      )}
    </>
  );
};

export default TopPerformers;
